import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { PageContainer } from '@/components/layout/PageContainer';
import { useToast } from '@/components/common/Toast';
import { useAuth } from '@/contexts/AuthContext';
import { notificationService, NotificationWithStatus } from '@/services/notification';
import { LoginPromptModal } from '@/components/common/LoginPromptModal';
import { useNotificationStore } from '@/store/notifications';
import { NotificationDetailModal } from '@/components/common/NotificationDetailModal';

export function NotificationPage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationWithStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationWithStatus | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const setUnreadCount = useNotificationStore((state) => state.setUnreadCount);
  const setInitialized = useNotificationStore((state) => state.setInitialized);

  const updateUnreadBadge = useCallback((items: NotificationWithStatus[]) => {
    const unread = items.filter((notification) => !notification.isRead).length;
    setUnreadCount(unread);
    setInitialized(true);
  }, [setUnreadCount, setInitialized]);

  useEffect(() => {
    const loadNotifications = async () => {
      if (!user) {
        setIsLoading(false);
        setShowLoginPrompt(true);
        return;
      }

      try {
        setIsLoading(true);
        const result = await notificationService.getNotifications(user.id);
        
        if (result.success && result.data) {
          setNotifications(result.data);
          updateUnreadBadge(result.data);
        } else {
          console.error('Failed to load notifications:', result.error);
          showToast('알림을 불러오는데 실패했습니다');
          updateUnreadBadge([]);
        }
      } catch (error) {
        console.error('Failed to load notifications:', error);
        showToast('알림을 불러오는데 실패했습니다');
        updateUnreadBadge([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
  }, [user, showToast, updateUnreadBadge]);

  const handleBack = () => navigate(-1);

  const handleLoginPromptClose = () => {
    setShowLoginPrompt(false);
    navigate(-1);
  };

  const handleLoginClick = () => {
    setShowLoginPrompt(false);
    navigate('/login');
  };

  const handleNotificationClick = async (notificationId: string) => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    const target = notifications.find((n) => n.id === notificationId);
    setSelectedNotification(target || null);
    setShowDetailModal(true);

    try {
      const result = await notificationService.markAsRead(user.id, notificationId);
      if (result.success) {
        setNotifications(prev => {
          const next = prev.map(notification => 
            notification.id === notificationId 
              ? { ...notification, isRead: true }
              : notification
          );
          updateUnreadBadge(next);
          return next;
        });
      }
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    try {
      const result = await notificationService.deleteNotification(user.id, notificationId);
      if (result.success) {
        setNotifications(prev => {
          const next = prev.filter(notification => notification.id !== notificationId);
          updateUnreadBadge(next);
          return next;
        });
        showToast('알림이 삭제되었습니다');
      } else {
        showToast('삭제 처리에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
      showToast('삭제 처리에 실패했습니다');
    }
  };

  const handleDeleteAll = async () => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    if (notifications.length === 0) {
      showToast('삭제할 알림이 없습니다');
      return;
    }

    const confirmDelete = window.confirm('모든 알림을 삭제하시겠습니까?');
    if (!confirmDelete) return;

    try {
      const result = await notificationService.deleteAllNotifications(user.id);
      if (result.success) {
        setNotifications([]);
        updateUnreadBadge([]);
        showToast('모든 알림이 삭제되었습니다');
      } else {
        showToast('전체 삭제에 실패했습니다');
      }
    } catch (error) {
      console.error('Failed to delete all notifications:', error);
      showToast('전체 삭제에 실패했습니다');
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user) {
      showToast('로그인이 필요합니다');
      return;
    }

    const unread = notifications.filter((n) => !n.isRead);
    if (unread.length === 0) {
      showToast('읽지 않은 알림이 없습니다');
      return;
    }

    try {
      await Promise.all(unread.map(n => notificationService.markAsRead(user.id!, n.id)));
      setNotifications(prev => {
        const next = prev.map(n => ({ ...n, isRead: true }));
        updateUnreadBadge(next);
        return next;
      });
      showToast('모두 읽음 처리했습니다');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      showToast('읽음 처리에 실패했습니다');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">알림을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  return (
    <div className="min-h-screen bg-white">
      <PageContainer>
        {/* 헤더 */}
        <div className="flex items-center justify-center relative mb-6">
          <button
            onClick={handleBack}
            className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <img
              src="/icons/icon_back.svg"
              alt="뒤로가기"
              className="w-6 h-6"
            />
          </button>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[#222222]">알림</h1>
            {unreadCount > 0 && (
              <p className="text-sm text-gray-600 mt-1">읽지 않은 알림 {unreadCount}개</p>
            )}
          </div>
        </div>

        {/* 액션 버튼 */}
        <div className="flex justify-end gap-3 mb-4">
          <button
            onClick={handleMarkAllAsRead}
            className="px-3 py-2 text-sm font-medium text-[#222222] border border-[#979797] rounded-lg hover:bg-gray-50 transition-colors"
          >
            모두 읽음
          </button>
          <button
            onClick={handleDeleteAll}
            className="px-3 py-2 text-sm font-medium text-[#222222] border border-[#979797] rounded-lg hover:bg-gray-50 transition-colors"
          >
            전체 삭제
          </button>
        </div>

        {/* 알림 목록 */}
        <div className="space-y-4">
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">알림이 없습니다</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "relative p-4 rounded-xl cursor-pointer transition-all border",
                  notification.isRead 
                    ? "bg-gray-50 border-gray-200"
                    : "bg-green-50 border-green-200 shadow-sm"
                )}
                onClick={() => handleNotificationClick(notification.id)}
              >
                {/* 삭제 버튼 */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteNotification(notification.id);
                  }}
                  className="absolute top-2 right-2 p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="text-gray-600"
                  >
                    <path
                      d="M12 4L4 12M4 4L12 12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* 알림 내용 */}
                <div className="pr-8">
                  <div className="flex items-start gap-2 mb-2">
                    {!notification.isRead && (
                      <span className="mt-0.5 inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-green-500 text-white">
                        NEW
                      </span>
                    )}
                    <h3 className="font-semibold text-[#222222] text-base">
                      {notification.title || '알림'}
                    </h3>
                  </div>
                  <p className="text-[#222222] leading-relaxed text-sm line-clamp-2">
                    {notification.message}
                  </p>
                  {notification.created_at && (
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(notification.created_at).toLocaleString('ko-KR')}
                    </p>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </PageContainer>
      
      <ToastContainer />
      
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={handleLoginPromptClose}
        onLogin={handleLoginClick}
      />

      <NotificationDetailModal
        isOpen={showDetailModal}
        notification={selectedNotification}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedNotification(null);
        }}
      />
    </div>
  );
}
